import React from 'react';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { pdfjs, Document, Page } from 'react-pdf';
import { useDispatch } from 'react-redux';

import DialogTransition from '../DialogTransition';

import type { ContentAttachmentJson } from '@/types/domain/notice/valueObjects/contentAttachment';

import { downloadAction } from '@/containers/Main/Content/Notice/thunk';
import { uiSlice } from '@/domain/ui/redux';

// 非legacyのbuildはPromise.withResolversを使うが、これはes2024以降対応のため、当面legacyのbuildを使用することとする
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const url2File = async (url: string, fileName: string) => {
  const blob = await (await fetch(url)).blob();
  return new File([blob], fileName, { type: blob.type });
};

const useWindowSize = (): number[] => {
  const [size, setSize] = React.useState([0, 0]);
  React.useLayoutEffect(() => {
    const updateSize = (): void => {
      setSize([window.innerWidth, window.innerHeight]);
    };
    window.addEventListener('resize', updateSize);
    updateSize();

    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
};

/**
 * プロパティ
 */
export type Props = {
  /** 開くか */
  isOpen: boolean,
  /** 添付 */
  attachment: ContentAttachmentJson,
  /** 閉じる */
  close: () => void,
}

const PdfViewer: React.FC<Props> = ({
  isOpen,
  attachment,
  close,
}) => {
  const dispatch = useDispatch();
  const [numPages, setNumPages] = React.useState<number>(1);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pdfFile, setPdfFile] = React.useState<File | undefined>(undefined);
  const [width, _height] = useWindowSize();

  React.useEffect(() => {
    if (attachment && !pdfFile) {
      const test = async () => {
        setPdfFile(await url2File(attachment.content, attachment.name));
      };

      test();
    }
  }, [pdfFile, attachment]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const download = () => {
    downloadAction(attachment);
    dispatch(uiSlice.actions.setMessage({ key: 'info', content: 'ダウンロードフォルダをご確認ください' }));
  };

  let page;
  if (currentPage <= numPages) {
    page = (
      <Page
        width={width - 50}
        key={`page_${currentPage}`}
        pageNumber={currentPage}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
    );
  }
  return (
    <Dialog
      open={isOpen}
      onClose={close}
      TransitionComponent={DialogTransition}
      fullScreen
    >
      <DialogContent>
        {pdfFile && (
          <Document
            file={pdfFile}
            onLoadSuccess={onDocumentLoadSuccess}
          >
            {page}
          </Document>
        )}
      </DialogContent>
      {numPages > 1 && (
        <DialogActions>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
          >
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage - 1 < 1}
              variant="contained"
              startIcon={<ArrowBackIosNewIcon />}
              color="secondary"
            >
              前のページ
            </Button>
            <div>{`${currentPage}/${numPages}`}</div>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage + 1 > numPages}
              variant="contained"
              endIcon={<ArrowForwardIosIcon />}
              color="secondary"
            >
              次のページ
            </Button>
          </div>
        </DialogActions>
      )}
      <DialogActions>
        <div>
          <Button
            variant="contained"
            onClick={download}
            style={{ marginLeft: '10px' }}
            startIcon={<DownloadIcon />}
          >
            ダウンロード
          </Button>
          <Button
            variant="contained"
            onClick={close}
            style={{ marginLeft: '10px' }}
          >
            閉じる
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default PdfViewer;
